/**
 * Trade Sound Effects
 *
 * Programmatic Web Audio API tones for trade notifications.
 * No external audio files required.
 *
 * Feature flag: VITE_ENABLE_TRADE_SOUNDS=true
 * User opt-in: localStorage key 'dextoro_trade_sounds_enabled'
 */

const FEATURE_ENABLED = import.meta.env.VITE_ENABLE_TRADE_SOUNDS === 'true';
const STORAGE_KEY = 'dextoro_trade_sounds_enabled';
const DEBOUNCE_MS = 300;
const WHALE_THRESHOLD_SOL = 5;

let audioCtx: AudioContext | null = null;
let lastPlayedAt = 0;

function getAudioContext(): AudioContext | null {
  if (!audioCtx) {
    try {
      audioCtx = new AudioContext();
    } catch {
      return null;
    }
  }
  return audioCtx;
}

/** Whether the trade sounds feature flag is enabled */
export const TRADE_SOUNDS_AVAILABLE = FEATURE_ENABLED;

/** Check if the user has opted in to trade sounds */
export function isTradeSoundEnabled(): boolean {
  if (!FEATURE_ENABLED) return false;
  try {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

/** Set user opt-in preference */
export function setTradeSoundEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, String(enabled));
  } catch {
    // localStorage unavailable
  }
}

/** Toggle user opt-in and return new state */
export function toggleTradeSound(): boolean {
  const next = !isTradeSoundEnabled();
  setTradeSoundEnabled(next);
  return next;
}

/** Play a short sine tone */
function playTone(frequency: number, durationMs: number, volume: number, startDelay = 0): void {
  const ctx = getAudioContext();
  if (!ctx) return;

  const osc = ctx.createOscillator();
  const gain = ctx.createGain();

  osc.type = 'sine';
  osc.frequency.value = frequency;
  gain.gain.value = volume;

  osc.connect(gain);
  gain.connect(ctx.destination);

  const start = ctx.currentTime + startDelay;
  const end = start + durationMs / 1000;

  // Quick fade out to avoid click
  gain.gain.setValueAtTime(volume, start);
  gain.gain.exponentialRampToValueAtTime(0.001, end);

  osc.start(start);
  osc.stop(end);
}

/**
 * Play a trade notification sound.
 *
 * - Regular buy: 880Hz sine, 120ms, volume 0.15
 * - Whale buy (>5 SOL): two-tone rising chime (660Hz → 880Hz), volume 0.2–0.25
 *
 * Gated by: feature flag, user opt-in, debounce, and tab visibility.
 */
export function playTradeSound(amountSol: number): void {
  if (!FEATURE_ENABLED) return;
  if (!isTradeSoundEnabled()) return;

  // Skip if tab is hidden
  if (typeof document !== 'undefined' && document.hidden) return;

  // Debounce
  const now = Date.now();
  if (now - lastPlayedAt < DEBOUNCE_MS) return;
  lastPlayedAt = now;

  if (amountSol >= WHALE_THRESHOLD_SOL) {
    // Whale sound: rising two-tone chime
    playTone(660, 150, 0.2);
    playTone(880, 200, 0.25, 0.12);
  } else {
    // Regular buy sound
    playTone(880, 120, 0.15);
  }
}
